import { isLocale, type Locale } from '@bitwize/i18n-messages';

/**
 * Remembering the visitor's language across visits.
 *
 * Two stores, deliberately, because neither one alone does the job:
 *
 *   - **localStorage** is the durable record. It survives cookie clearing, is
 *     never sent to the server, and is the store the preference actually lives
 *     in.
 *   - **A mirrored cookie** exists for exactly one reason: the SSR tier decides
 *     where an unprefixed `/` sends a visitor, and the server cannot read
 *     localStorage. Without the mirror, a returning Arabic reader lands on
 *     English every time they type the bare domain.
 *
 * Both are written by the client. The server only ever reads. That asymmetry is
 * what keeps every rendered page cacheable: a response that carries `Set-Cookie`
 * is refused by most shared caches, and the SSR page cache in M3
 * (docs/performance.md) depends on those responses staying cacheable. The one
 * route that reads the cookie — the `/` redirect — is uncacheable regardless,
 * because it already varies on `Accept-Language`.
 *
 * The cost of that choice: a visitor with JavaScript disabled is negotiated
 * afresh on each visit. Their language switching still works, because every
 * public URL carries its locale in the path.
 */

export const LOCALE_STORAGE_KEY = 'hs-locale';
export const LOCALE_COOKIE_NAME = 'hs-locale';

/** One year. A language preference does not go stale. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Read the preference out of a `Cookie` request header.
 *
 * Returns `null` for anything unrecognised rather than guessing, so a stale
 * cookie naming a locale that has since been removed falls through to
 * negotiation instead of producing a 404 redirect loop.
 */
export function readLocaleCookie(cookieHeader: string | null | undefined): Locale | null {
  if (!cookieHeader) return null;

  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator === -1) continue;

    const name = part.slice(0, separator).trim();
    if (name !== LOCALE_COOKIE_NAME) continue;

    const value = decodeURIComponent(part.slice(separator + 1).trim());
    return isLocale(value) ? value : null;
  }

  return null;
}

/**
 * Read the durable preference. Client only.
 *
 * `localStorage` throws rather than returning null in a partitioned or
 * private-browsing context, so every access is guarded. Having no stored
 * preference is a normal state, not an error worth reporting.
 */
export function readStoredLocale(): Locale | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Record the visitor's language in both stores. Client only.
 *
 * `SameSite=Lax` because the cookie is read on a top-level navigation to `/`
 * and nowhere else. It is intentionally *not* `HttpOnly`: this code is what
 * writes it. It carries no identity and no session — only which of two public
 * languages to render — so it is not a credential and needs no consent banner.
 */
export function rememberLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Private-browsing or partitioned storage. The cookie below still works for
    // the rest of the session, which is the better of the two failure modes.
  }

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${LOCALE_COOKIE_NAME}=${locale}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE}` +
    `; SameSite=Lax${secure}`;
}
